import _ from "lodash";
import React from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { compose, graphql } from "lib/apolloClient";

import { Popover, ArrowContainer } from "@toddle-edu/react-tiny-popover";
import { TextInput, Tooltip } from "@toddle-edu/ds-web";
import { InformationOutlined } from "@toddle-edu/ds-icons";
import UIBaseComponent from "UIComponents/UIBaseComponent";

import {
  updateAssessment,
  updateAssessmentInRedux,
  saveAssessmentToolUpdateStatusInRedux,
  registerAssessmentToolUpdateActivity,
} from "Assessments/modules/AssessmentModule";
import {
  updateScoreToolData,
  writeAssessmentScoreToolDataInCache,
} from "modules/AssessmentTool/AssessmentToolModule";
import {
  getSelectedAcademicYearId,
  getSelectedCurriculumId,
  getCurrentCurriculumProgramType,
  getOrganizationId,
} from "modules/CommonSelectors";
import { getIsScoreV2 } from "routes/routes/Administrator/routes/DPAcademicSetup/routes/AssessmentSetup/modules/utils";
import {
  getIsConfigurationEnabledForCPAY,
  getSettingValue,
} from "modules/PermissionModule";
import { USE_WEIGHTAGE_FOR_CATEGORY_SETTING } from "GradingSetup/modules/Utils";
import {
  getSelectedSubjectIdMemoize,
  getAssociatedEntitiesVariables,
} from "../MaxScoreTool/Utils";
import {
  getScoreDetailsMemoize,
  checkCategorySetApplicableTo,
} from "ScoreCategories/modules/ScoreCategoriesModule";
import { getCourseDetailsFromCache } from "ProgressSummary/modules/ProgressSummaryGraphqlHelpers";
import { getOrganizationPointSetFromCache } from "ScoreCategories/modules/ScoreCategoriesGraphqlHelpers";
import { getAssessmentDetailsFromCache } from "Assessments/modules/AssessmentGraphqlHelpers";
import {
  getOrganizationPointSetByAcademicTermId,
  getOrganizationPointSetData,
} from "../AssessmentCategory/utils";
import { colors } from "Constants";
import {
  getIsCustomWeights,
  getIsCustomWeightsV2,
} from "ClassRoom/modules/utils";
import { getIsScoringToolPresent } from "Utils";
import {
  ASSESSMENT_GRADE_BOUNDARY,
  WEIGHTS_PROPORTIONAL,
} from "Constants/stringConstants";
import { getAssessmentGradingConfigurationsQuery } from "GradingSetupV2/modules/GradingSetupV2Queries";
import { USE_NEW_GRADING_SETUP_IN_COURSE_PERM } from "Constants/permissionConstants";
import { checkAssignmentContributionMethodValue } from "GradingSetupV2/modules/Utils";

import classes from "./MaxScore.scss";

const styles = {
  textInputStyles: {
    width: "auto",
    maxWidth: "144px",
  },
  popoverContainerStyles: { zIndex: 1500, top: "-35px" },
  overlayInnerStyle: { maxWidth: "300px", textAlign: "center" },
};

const CustomPopOver = props => {
  const { showPopOver, setShowPopOver, warningLabel, childrenRef } = props;
  return (
    <Popover
      isOpen={showPopOver}
      align={"center"}
      positions={["bottom"]}
      reposition={true}
      boundaryInset={20}
      onClickOutside={() => {
        setShowPopOver(false);
      }}
      containerStyle={styles.popoverContainerStyles}
      padding={40}
      content={({ position, childRect, popoverRect }) => (
        <ArrowContainer
          position={position}
          childRect={childRect}
          popoverRect={popoverRect}
          arrowColor={colors.white}
          arrowSize={10}
          className="popover-arrow-container"
          arrowClassName="popover-arrow"
        >
          <div className={classes.warningCard}>
            <span>
              <InformationOutlined size="xx-small" />
            </span>
            <div className={"text-body-2"}>{warningLabel}</div>
          </div>
        </ArrowContainer>
      )}
      childrenRef={childrenRef}
      isChildrenRefPassed={true}
    ></Popover>
  );
};

class MaxScore extends UIBaseComponent {
  constructor(props) {
    super(props);
    this.state = {
      maxScore: "",
      isError: false,
      showPopOver: false,
    };

    this.popOverRef = React.createRef();

    if (props?.customRef) {
      props.customRef(this);
    }
  }

  isValid = () => {
    const {
      parentId,
      isCollapsibleAssessmentTool,
      isCustomWeights,
      collaboratorsFilter,
    } = this.props;
    const { maxScore, isError } = this.state;

    if (isCollapsibleAssessmentTool || !isCustomWeights) {
      return 0;
    }

    const assessmentDetails = getAssessmentDetailsFromCache({
      id: parentId,
      collaboratorsFilter,
    });
    const weightage = _.get(assessmentDetails, "weightage");

    if (!_.isEmpty(_.toString(weightage)) && _.isEmpty(maxScore)) {
      this.updateState({ key: "isError", value: true });
      return 1;
    }

    if (isError) {
      this.updateState({ key: "isError", value: false });
    }
    return 0;
  };

  getDefaultLtiScore = () => {
    const { ltiScoreMaximum } = this.props;
    return ltiScoreMaximum ? _.toString(ltiScoreMaximum) : "100";
  };
  async componentDidUpdate(prevProps) {
    const {
      isLtiToolAdded,
      value,
      mode,
      ltiScoreMaximum,
      onDeleteScoreTool,
      scoreToolId,
      assessmentToolId,
    } = this.props;

    if (
      isLtiToolAdded &&
      !prevProps.isLtiToolAdded &&
      _.isEmpty(value) &&
      mode === "edit"
    ) {
      await this.updateMaxScore({
        ltiPrefilledValue: this.getDefaultLtiScore(),
      });
      this.onBlur();
    }

    if (!isLtiToolAdded && prevProps.isLtiToolAdded) {
      if (onDeleteScoreTool) {
        onDeleteScoreTool({
          id: assessmentToolId || scoreToolId,
          type: "SCORE",
        });
      }
    }

    if (
      isLtiToolAdded &&
      prevProps.isLtiToolAdded &&
      ltiScoreMaximum !== prevProps.ltiScoreMaximum &&
      mode === "edit"
    ) {
      await this.updateMaxScore({
        ltiPrefilledValue: this.getDefaultLtiScore(),
      });
      this.onBlur();
    }
  }

  componentDidMount = async () => {
    const {
      value,
      isCollapsibleAssessmentTool,
      updateAssessmentInRedux,
      isLtiToolAdded,
      mode,
    } = this.props;

    if (isLtiToolAdded && _.isEmpty(value) && mode === "edit") {
      await this.updateMaxScore({
        ltiPrefilledValue: this.getDefaultLtiScore(),
      });
      this.onBlur();
    } else {
      this.updateState({ key: "maxScore", value });
    }

    if (!isCollapsibleAssessmentTool && !_.isEmpty(value)) {
      updateAssessmentInRedux({ maxScore: value });
    }
  };

  componentWillUnmount = () => {
    const { updateAssessmentInRedux, isCollapsibleAssessmentTool } = this.props;

    if (!isCollapsibleAssessmentTool) {
      updateAssessmentInRedux({ maxScore: null });
    }
  };

  updateState = ({ key, value }) => {
    this.setState(prevState => {
      return {
        ...prevState,
        [key]: value,
      };
    });
  };

  debouncedUpdateScoreToolDataInCache = _.debounce(maxScore => {
    const {
      isCollapsibleAssessmentTool,
      writeAssessmentScoreToolDataInCache,
      scoreToolId,
      parentId,
      parentType = "ASSESSMENT",
      assessmentId,
    } = this.props;

    writeAssessmentScoreToolDataInCache({
      id: scoreToolId,
      maxScore,
      parentId,
      parentType,
      assessmentId: assessmentId || parentId,
      createNewIfNotExists: !isCollapsibleAssessmentTool,
    });
  }, 300);

  updateMaxScore = params => {
    const {
      assessmentCategories,
      category,
      useWeightedCategories,
      isCollapsibleAssessmentTool,
      assignmentId,
      saveAssessmentToolUpdateStatusInRedux,
    } = this.props;
    const { isError } = this.state;
    const { e, ltiPrefilledValue } = params;
    const maxScore = ltiPrefilledValue
      ? ltiPrefilledValue
      : _.get(e, "target.value");
    const maxScoreInt = parseInt(maxScore);
    // allow only digits
    const reg = /^\d+$/;

    if ((reg.test(maxScore) && maxScoreInt <= 1000) || !maxScore) {
      this.updateState({ key: "maxScore", value: maxScore });
      saveAssessmentToolUpdateStatusInRedux({
        assignmentId,
        toolType: "SCORE",
      });

      if (isError) {
        this.updateState({ key: "isError", value: false });
      }

      // show popover only if weighted categories is selected and
      // assessment categories exists and user didn't choose any category
      if (
        useWeightedCategories &&
        !_.isEmpty(assessmentCategories) &&
        _.isEmpty(category) &&
        !isCollapsibleAssessmentTool
      ) {
        this.updateState({ key: "showPopOver", value: true });
      }

      this.debouncedUpdateScoreToolDataInCache(maxScore);
    }
  };

  onBlur = () => {
    const { maxScore } = this.state;
    const {
      updateAssessment,
      updateScoreToolData,
      isCollapsibleAssessmentTool,
      isDPWithGradeScale,
      scoreToolId,
      parentId,
      parentType,
      assessmentId,
      assignmentId,
      registerAssessmentToolUpdateActivity,
    } = this.props;

    registerAssessmentToolUpdateActivity({
      assignmentId,
      toolType: "SCORE",
    });

    if (isCollapsibleAssessmentTool) {
      updateScoreToolData({
        id: scoreToolId,
        maxScore,
        parentId,
        parentType,
        assessmentId,
      });
      return;
    }

    this.updateState({ key: "showPopOver", value: false });

    if (isDPWithGradeScale && !_.isEmpty(_.toString(maxScore))) {
      updateAssessment({
        hasFields: false,
        weightage: parseFloat(maxScore),
        scoreToolData: {
          isUsingScore: !_.isEmpty(maxScore),
          maxScore: maxScore ? maxScore : "",
        },
      });
    } else {
      updateAssessment({
        hasFields: false,
        scoreToolData: {
          isUsingScore: !_.isEmpty(maxScore),
          maxScore: maxScore ? maxScore : "",
        },
      });
    }
  };

  renderMaxScore = () => {
    const { t, mode, testId, assessmentTools } = this.props;
    const { showPopOver, isError } = this.state;

    const isViewMode = mode === "view";

    const isStandardBasedRubricPresent = getIsScoringToolPresent(
      assessmentTools
    );

    const disabled = isViewMode || isStandardBasedRubricPresent;

    return (
      <>
        <Tooltip
          tooltip={t("classRoom:only_one_scoring_based_tool_is_allowed_msg")}
          visible={isStandardBasedRubricPresent}
          placement="top"
          dsVersion="2.0"
          overlayInnerStyle={styles.overlayInnerStyle}
        >
          <TextInput
            size={"medium"}
            placeholder={t("common:eg_100")}
            style={styles.textInputStyles}
            value={this.state.maxScore}
            onChange={e => this.updateMaxScore({ e })}
            disabled={disabled}
            onBlur={this.onBlur}
            testId={testId}
            ref={this.popOverRef}
            error={isError}
          />
        </Tooltip>

        {showPopOver ? (
          <CustomPopOver
            showPopOver={showPopOver}
            setShowPopOver={value => {
              this.updateState({ key: "showPopOver", value });
            }}
            warningLabel={t("classRoom:max_score_without_category_warning")}
            childrenRef={this.popOverRef}
          />
        ) : null}
      </>
    );
  };

  renderEdit = this.renderMaxScore;
  renderView = this.renderMaxScore;
}

const mapActionCreators = {
  updateAssessment,
  updateAssessmentInRedux,
  updateScoreToolData,
  writeAssessmentScoreToolDataInCache,
  saveAssessmentToolUpdateStatusInRedux,
  registerAssessmentToolUpdateActivity,
};

const mapStateToProps = (state, ownProps) => {
  const curriculumProgramId =
    getSelectedCurriculumId(state) || _.get(ownProps, "curriculumProgram.id");
  const curriculumType =
    getCurrentCurriculumProgramType(state) ||
    _.get(ownProps, "curriculumProgram.type");

  const organizationId = getOrganizationId(state);

  const selectedCourseId =
    _.get(state, "teacher.selected_class.selected_course", "") ||
    ownProps?.selectedCourseId;
  const paramsCourseId = _.get(ownProps, "params.course_id");
  const courseId = paramsCourseId ? paramsCourseId : selectedCourseId;
  const academicYearId = getSelectedAcademicYearId(state);

  const courseDetails = getCourseDetailsFromCache({
    id: courseId,
    courseGradeFilters: {
      academicYearId,
    },
  });
  const grades = _.get(courseDetails, "grades", []);
  const gradeId =
    _.get(courseDetails, "primaryGrade.id", "") ||
    _.get(_.head(grades), "id", "");
  const { selectedSubjectId } = getSelectedSubjectIdMemoize({
    dependentFieldValue: _.get(ownProps, "dependentFieldValue"),
  });

  const isScoreV2 = getIsScoreV2({ curriculumType });
  const isCourseApplicableToCategorySet =
    checkCategorySetApplicableTo({
      organizationId,
    }) === "COURSE";
  const isCategoryDropdownEnabled =
    selectedSubjectId && (isCourseApplicableToCategorySet ? courseId : true);

  const pointSetVariables = {
    id: organizationId,
    filters: {
      types: "SCORE",
      curriculumProgramIds: curriculumProgramId,
      associatedEntites: [
        {
          type: "SUBJECT",
          ids: selectedSubjectId,
        },
        getAssociatedEntitiesVariables({
          gradeId,
          isCourseApplicableToCategorySet,
          courseId,
        }),
      ],
    },
  };

  const organizationPointSetData = isCategoryDropdownEnabled
    ? getOrganizationPointSetFromCache({
        variables: pointSetVariables,
      })
    : {};

  const organizationPointSet = _.get(
    organizationPointSetData,
    "pointAssessmentSets.edges",
    []
  );

  const filteredOrganizationPointSet = isScoreV2
    ? getOrganizationPointSetByAcademicTermId({
        organizationPointSet,
        termId: _.get(ownProps, "academicTermId"),
      })
    : getOrganizationPointSetData({ organizationPointSet });

  const assessmentCategories = _.get(
    filteredOrganizationPointSet,
    "categories",
    []
  );

  const useWeightedCategories = getSettingValue({
    name: USE_WEIGHTAGE_FOR_CATEGORY_SETTING,
    currentCurriculumProgramId: curriculumProgramId,
  });

  const { maxScore: value } = getScoreDetailsMemoize({
    assessmentTools: _.get(ownProps, "assessmentTools", []),
  });

  const collaboratorsFilter = {
    userAcademicYearId: academicYearId,
  };

  const isUsingNewGradingSetupInCourses = getIsConfigurationEnabledForCPAY({
    configuration: USE_NEW_GRADING_SETUP_IN_COURSE_PERM,
    curriculumProgramId,
    academicYearId: academicYearId,
  });

  // Derive isDPWithGradeScale internally so MaxScore always knows whether
  // it is in the DP + IB grade scale + proportional weight flow, regardless
  // of which render path instantiates it (CollapsibleAssessmentTools or
  // FieldComponent/MiddlePane which is used for DP).
  const isDP = curriculumType === "IB_DP";
  const subjectIds = _.isArray(ownProps.dependentFieldValue)
    ? ownProps.dependentFieldValue
    : _.compact([ownProps.dependentFieldValue]);
  const isWeightProportionalToMaxScore =
    isUsingNewGradingSetupInCourses &&
    checkAssignmentContributionMethodValue({
      learningCourseId: ownProps.learningCourseId,
      curriculumProgramType: curriculumType,
      subjectIds,
      primaryGradeId: gradeId,
      methods: [WEIGHTS_PROPORTIONAL],
      academicYearId,
    });
  // assessmentTools can arrive as a plain edge-array (from CollapsibleAssessmentTools)
  // or as a full GraphQL connection object { edges: [...] } (from FieldComponent/MiddlePane).
  const rawAssessmentTools = _.get(ownProps, "assessmentTools", []);
  const assessmentToolEdges = _.isArray(rawAssessmentTools)
    ? rawAssessmentTools
    : _.get(rawAssessmentTools, "edges", []);

  const hasGradeBoundaryTool = _.some(
    assessmentToolEdges,
    ({ node }) =>
      _.get(node, "assessmentToolType") === ASSESSMENT_GRADE_BOUNDARY
  );
  const isDPWithGradeScale =
    isDP &&
    isUsingNewGradingSetupInCourses &&
    isWeightProportionalToMaxScore &&
    hasGradeBoundaryTool;

  return {
    value: value || _.get(ownProps, "maxScore", ""),
    assessmentCategories,
    useWeightedCategories,
    isCustomWeights: getIsCustomWeights({
      curriculumProgramId,
      curriculumProgramType: curriculumType,
    }),
    collaboratorsFilter,
    isUsingNewGradingSetupInCourses,
    isDPWithGradeScale,
  };
};

MaxScore.propTypes = {
  ...UIBaseComponent.propTypes,
  isCollapsibleAssessmentTool: PropTypes.bool,
  isDPWithGradeScale: PropTypes.bool,
};

MaxScore.defaultProps = {
  ...UIBaseComponent.defaultProps,
  isCollapsibleAssessmentTool: false,
  isDPWithGradeScale: false,
};

export default compose(
  connect(mapStateToProps, mapActionCreators),
  graphql(getAssessmentGradingConfigurationsQuery, {
    name: "getAssessmentGradingConfigurations",
    skip: ({ assessmentId, isUsingNewGradingSetupInCourses }) =>
      !isUsingNewGradingSetupInCourses || _.isEmpty(assessmentId),
    options: ({ assessmentId }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        id: assessmentId,
      },
    }),
    props({ getAssessmentGradingConfigurations }) {
      const gradingConfigurations = _.get(
        getAssessmentGradingConfigurations,
        "node.gradingConfigurations",
        []
      );

      const isCustomWeightsV2 = getIsCustomWeightsV2(gradingConfigurations);

      return { isCustomWeights: isCustomWeightsV2 };
    },
  })
)(MaxScore);
